import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { IdleTimeoutBanner } from "./IdleTimeoutBanner";

// Timings (ms). 60-minute total idle, 5-minute warning window.
const IDLE_LIMIT_MS = 60 * 60 * 1000;
const WARN_AT_MS = 55 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const TICK_MS = 1000;

interface IdleCtx {
  remainingMs: number | null; // null when banner not shown
}
const IdleContext = createContext<IdleCtx>({ remainingMs: null });
export const useIdleTimeout = () => useContext(IdleContext);

export function IdleTimeoutProvider({ children }: { children: ReactNode }) {
  const { realUser, signOut } = useAuth();
  const navigate = useNavigate();
  const lastActivityRef = useRef<number>(Date.now());
  const signedOutRef = useRef(false);
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const isAuthed = !!realUser;

  const recordActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityRef.current = now;
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      setRemainingMs(null);
      signedOutRef.current = false;
      return;
    }

    lastActivityRef.current = Date.now();
    signedOutRef.current = false;

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];
    const onActivity = () => recordActivity();
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const onVisibility = () => {
      // When the tab becomes visible again, re-evaluate immediately. We do NOT
      // bump lastActivity — true idleness across a tab switch still counts.
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Also treat successful fetches as activity (covers background polling-as-use).
    const origFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await origFetch(...args);
      if (res.ok) recordActivity();
      return res;
    };

    const tick = async () => {
      if (signedOutRef.current) return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_LIMIT_MS) {
        signedOutRef.current = true;
        setRemainingMs(null);
        try {
          await supabase.auth.signOut({ scope: "local" });
        } catch {
          /* ignore — navigate regardless */
        }
        navigate("/logout-confirmation?reason=idle");
        return;
      }
      if (elapsed >= WARN_AT_MS) {
        setRemainingMs(IDLE_LIMIT_MS - elapsed);
      } else if (remainingMs !== null) {
        setRemainingMs(null);
      }
    };

    const interval = window.setInterval(tick, TICK_MS);
    tick();

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
      window.fetch = origFetch;
      window.clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  // Suppress lint about signOut/navigate identity — re-mounting the listeners
  // on every auth render would lose the activity timer.
  void signOut;

  return (
    <IdleContext.Provider value={{ remainingMs }}>
      {children}
      {isAuthed && remainingMs !== null && <IdleTimeoutBanner remainingMs={remainingMs} />}
    </IdleContext.Provider>
  );
}
